import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Calendar, TrendingUp, Clock, User, MessageCircle,
  Award, Target, Lightbulb, Phone, ChevronRight, Activity,
  FileText, Smile, TrendingDown, Sun, Sparkles,
  MessageSquare, ArrowRight, BarChart2, CheckCircle
} from 'lucide-react';
import ParentNav from '../components/ParentNav';
import UpcomingMeetingsWidget from '../components/UpcomingMeetingsWidget';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MOOD_EMOJI = {
  very_happy: '😄',
  happy: '😊',
  neutral: '😐',
  sad: '😔',
  very_sad: '😢',
};

const MOOD_COLOR = {
  very_happy: 'bg-green-100 text-green-700 border-green-300',
  happy: 'bg-emerald-100 text-emerald-700 border-emerald-300',
  neutral: 'bg-yellow-100 text-yellow-700 border-yellow-300',
  sad: 'bg-orange-100 text-orange-700 border-orange-300',
  very_sad: 'bg-red-100 text-red-700 border-red-300',
};

const getMoodEmoji = (mood) => MOOD_EMOJI[mood] || '😐';
const getMoodColor = (mood) => MOOD_COLOR[mood] || 'bg-gray-100 text-gray-700';
const getTrendIcon = (trend) =>
  trend === 'improving' ? <TrendingUp className="w-4 h-4 text-green-500" />
    : trend === 'declining' ? <TrendingDown className="w-4 h-4 text-red-500" />
    : <Activity className="w-4 h-4 text-yellow-500" />;

const computeNextUpcomingDate = (childrenList) => {
  const times = childrenList
    .flatMap((c) => c.upcoming_appointments ? c.upcoming_appointments : [])
    .map((a) => a && a.scheduled_time ? a.scheduled_time : null)
    .filter((t) => t !== null);
  if (times.length === 0) return null;
  return new Date([...times].sort()[0]);
};

const EMPTY_DATA = { parent: {}, children: [], summary: {} };

const ParentDashboard = () => {
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState(EMPTY_DATA);
  const [hasData, setHasData] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedChild, setSelectedChild] = useState(null);
  const [monthlyReports, setMonthlyReports] = useState([]);

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('parent_token')}` }
  });

  useEffect(() => {
    const token = localStorage.getItem('parent_token');
    if (!token) {
      navigate('/parent/login');
      return;
    }
    loadDashboard();
  }, [navigate]);

  const loadDashboard = async () => {
    try {
      const [dashRes, monthlyRes] = await Promise.allSettled([
        axios.get(`${BACKEND_URL}/api/parent/dashboard`, getAuthHeaders()),
        axios.get(`${BACKEND_URL}/api/parent/monthly-reports`, getAuthHeaders()),
      ]);
      const response = dashRes.status === 'fulfilled' ? dashRes : null;
      if (!response) throw new Error('Dashboard failed');
      const dashData = response.value;
      if (monthlyRes.status === 'fulfilled') {
        setMonthlyReports(monthlyRes.value.data.reports || []);
      }
      setDashboardData(dashData.data);
      setHasData(true);
      if (dashData.data.children.length > 0) {
        setSelectedChild(dashData.data.children[0]);
      }
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem('parent_token');
        navigate('/parent/login');
      }
      console.error('Error loading dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const { parent, children, summary } = dashboardData;
  const nextUpcomingDate = computeNextUpcomingDate(children || []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-rose-50">
      <ParentNav />

      {loading && (
        <div className="pt-20 pb-20 px-4 flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-orange-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading your dashboard...</p>
          </div>
        </div>
      )}

      {!loading && !hasData && (
        <div className="pt-20 pb-20 px-4 flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <p className="text-gray-600">Unable to load dashboard. Please try again.</p>
            <Button onClick={loadDashboard} className="mt-4 bg-gradient-to-r from-orange-500 to-rose-500 hover:from-orange-600 hover:to-rose-600">Retry</Button>
          </div>
        </div>
      )}

      {!loading && hasData && (
        <>
      {/* Header */}
      <div className="bg-gradient-to-r from-orange-500 via-rose-500 to-amber-500 text-white pt-20 pb-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Sun className="w-5 h-5 text-amber-100" />
              <p className="text-orange-50 text-sm">Good {new Date().getHours() < 12 ? 'morning' : new Date().getHours() < 17 ? 'afternoon' : 'evening'}</p>
            </div>
            <h1 className="text-3xl font-bold mb-2" data-testid="parent-name">{parent.name}</h1>
            <p className="text-orange-50">Track your child&apos;s emotional journey</p>
          </div>

          {/* Summary Cards — Sessions This Month + Next Upcoming */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-8 max-w-2xl" data-testid="summary-cards">
            <Card className="bg-white/15 backdrop-blur border-white/20 text-white">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-orange-50 text-xs">Sessions This Month</p>
                    <p className="text-2xl font-bold mt-1" data-testid="sessions-this-month">{summary.total_sessions_this_month}</p>
                  </div>
                  <Clock className="w-8 h-8 text-amber-100" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white/15 backdrop-blur border-white/20 text-white">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-orange-50 text-xs">Upcoming Session</p>
                    <p className="text-xl font-bold mt-1" data-testid="next-upcoming-date">
                      {nextUpcomingDate
                        ? nextUpcomingDate.toLocaleString(undefined, { weekday: 'short', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
                        : 'None scheduled'}
                    </p>
                  </div>
                  <Calendar className="w-8 h-8 text-amber-100" />
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Children Selector */}
      <div className="max-w-7xl mx-auto px-4 -mt-10 mb-6 relative z-10">
        <div className="flex gap-4 overflow-x-auto pb-2 scrollbar-hide">
          {children.map((child) => (
            <Card
              key={child.id}
              onClick={() => setSelectedChild(child)}
              className={`cursor-pointer transition-all min-w-[280px] ${
                selectedChild?.id === child.id
                  ? 'ring-2 ring-orange-500 shadow-xl bg-white scale-[1.02]'
                  : 'hover:shadow-lg bg-white/95 hover:bg-white'
              }`}
              data-testid={`child-card-${child.id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-14 h-14 rounded-full bg-gradient-to-br from-orange-400 to-rose-500 flex items-center justify-center text-white text-xl font-bold shadow-lg">
                    {child.name?.charAt(0) || 'C'}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-gray-900">{child.name}</h3>
                    <p className="text-sm text-gray-500">Age {child.age} • {child.grade || child.class || 'Student'}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <Activity className="w-3 h-3 text-emerald-500" />
                      <span className="text-xs text-gray-600">{child.sessions_this_month || 0} sessions this month</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Main Content */}
      {selectedChild && (
        <div className="max-w-7xl mx-auto px-4 pb-12 space-y-6">
          {/* Upcoming meetings widget — shown above the main grid so it's the first thing seen */}
          <UpcomingMeetingsWidget role="parent" token={localStorage.getItem('parent_token')} />

          <div className="grid lg:grid-cols-3 gap-6">
            
            {/* Left Column - Today's Insight & Conversation Starters */}
            <div className="lg:col-span-2 space-y-6">
              
              {/* Today's Insight Card */}
              <Card className="border-l-4 border-l-orange-500 shadow-lg" data-testid="todays-insight">
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Sparkles className="w-5 h-5 text-orange-500" />
                    Today&apos;s Insight
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {selectedChild.todays_insight ? (
                    <div className="space-y-3">
                      <p className="text-gray-700 leading-relaxed">
                        {selectedChild.todays_insight.content || selectedChild.todays_insight.summary}
                      </p>
                      <p className="text-xs text-gray-500">
                        {new Date(selectedChild.todays_insight.created_at).toLocaleTimeString('en-US', {hour: '2-digit', minute: '2-digit'})}
                      </p>
                    </div>
                  ) : (
                    <div className="text-center py-6">
                      <Lightbulb className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                      <p className="text-gray-500">No insight available today yet</p>
                      <p className="text-sm text-gray-400 mt-1">Check back after your child&apos;s next session!</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Conversation Starters */}
              <Card className="shadow-lg" data-testid="conversation-starters">
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <MessageSquare className="w-5 h-5 text-orange-500" />
                    Conversation Starters
                  </CardTitle>
                  <p className="text-sm text-gray-500">Ideas to connect with {selectedChild.name?.split(' ')[0]}</p>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {selectedChild.conversation_starters?.map((starter, idx) => (
                      <div key={idx} className="flex items-start gap-3 p-3 bg-orange-50 rounded-lg hover:bg-orange-100 transition-colors">
                        <div className="w-8 h-8 rounded-full bg-orange-500 text-white flex items-center justify-center text-sm font-bold flex-shrink-0">
                          {idx + 1}
                        </div>
                        <p className="text-gray-700 text-sm leading-relaxed">{starter}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Weekly Summary */}
              <Card className="shadow-lg" data-testid="weekly-summary">
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Calendar className="w-5 h-5 text-orange-500" />
                    This Week&apos;s Summary
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="text-center p-4 bg-orange-50 rounded-xl">
                      <p className="text-3xl font-bold text-orange-600">
                        {selectedChild.weekly_summary?.sessions_this_week || 0}
                      </p>
                      <p className="text-xs text-gray-600 mt-1">Sessions</p>
                    </div>
                    <div className="text-center p-4 bg-rose-50 rounded-xl">
                      <p className="text-3xl font-bold text-rose-600">
                        {selectedChild.weekly_summary?.avg_mood ? selectedChild.weekly_summary.avg_mood.toFixed(1) : '—'}
                      </p>
                      <p className="text-xs text-gray-600 mt-1">Avg Mood</p>
                    </div>
                    <div className="text-center p-4 bg-amber-50 rounded-xl">
                      <div className="flex items-center justify-center gap-1">
                        {getTrendIcon(selectedChild.weekly_summary?.mood_trend)}
                        <p className="text-lg font-semibold text-amber-600 capitalize">
                          {selectedChild.weekly_summary?.mood_trend || 'Stable'}
                        </p>
                      </div>
                      <p className="text-xs text-gray-600 mt-1">Mood Trend</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Recent Sessions */}
              <Card className="shadow-lg">
                <CardHeader className="pb-2">
                  <div className="flex justify-between items-center">
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <FileText className="w-5 h-5 text-orange-500" />
                      Recent Sessions
                    </CardTitle>
                    <span className="text-sm text-gray-500">
                      {selectedChild.session_count || 0} total
                    </span>
                  </div>
                </CardHeader>
                <CardContent>
                  {selectedChild.recent_sessions && selectedChild.recent_sessions.length > 0 ? (
                    <div className="space-y-3">
                      {selectedChild.recent_sessions.slice(0, 5).map((session, index) => (
                        <div 
                          key={session.id || index} 
                          className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                        >
                          <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                            <Calendar className="w-5 h-5 text-orange-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-gray-900">
                              {new Date(session.session_date).toLocaleDateString('en-US', {
                                weekday: 'long',
                                month: 'short',
                                day: 'numeric'
                              })}
                            </p>
                            <p className="text-sm text-gray-500 truncate">
                              {session.observer_name ? `with ${session.observer_name}` : 'Listening session'}
                              {session.duration_minutes ? ` • ${session.duration_minutes} mins` : ''}
                            </p>
                          </div>
                          {session.mood_rating && (
                            <span className="text-lg" title={`Mood: ${session.mood_rating}/5`}>
                              {session.mood_rating >= 4 ? '😊' : session.mood_rating >= 3 ? '😐' : '😔'}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                      <p className="text-gray-500">No sessions recorded yet</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Right Column - Observer, Mood, Quick Actions */}
            <div className="space-y-6">
              
              {/* Observer Info */}
              <Card className="shadow-lg">
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Phone className="w-5 h-5 text-orange-500" />
                    Assigned Observer
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {selectedChild.observer ? (
                    <div className="space-y-3">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center">
                          <User className="w-6 h-6 text-orange-600" />
                        </div>
                        <div>
                          <p className="font-semibold">{selectedChild.observer.name}</p>
                          <p className="text-sm text-gray-500">{selectedChild.observer.email}</p>
                        </div>
                      </div>
                      <p className="text-sm text-gray-600 bg-orange-50 p-3 rounded-lg">
                        {selectedChild.name?.split(' ')[0]} speaks with {selectedChild.observer.name?.split(' ')[0]} during their daily listening sessions.
                      </p>
                    </div>
                  ) : (
                    <p className="text-gray-500 text-sm">No observer assigned yet</p>
                  )}
                </CardContent>
              </Card>

              {/* Mood History */}
              <Card className="shadow-lg" data-testid="mood-history">
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Smile className="w-5 h-5 text-yellow-500" />
                    Mood This Week
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {selectedChild.mood_entries && selectedChild.mood_entries.length > 0 ? (
                    <div className="space-y-2">
                      {selectedChild.mood_entries.slice(0, 5).map((entry, idx) => (
                        <div key={idx} className={`flex items-center justify-between p-3 rounded-lg border ${getMoodColor(entry.mood)}`}>
                          <div className="flex items-center gap-2">
                            <span className="text-2xl">{getMoodEmoji(entry.mood)}</span>
                            <div>
                              <p className="text-sm font-medium capitalize">{entry.mood?.replace('_', ' ')}</p>
                              <p className="text-xs opacity-75">{new Date(entry.logged_date).toLocaleDateString('en-US', {weekday: 'short', month: 'short', day: 'numeric'})}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-6">
                      <Smile className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                      <p className="text-gray-500 text-sm">Mood tracking starts after sessions begin</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Quick Actions */}
              <Card className="shadow-lg">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg">Quick Actions</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <Button
                    onClick={() => navigate('/parent/daily-insight')}
                    variant="outline"
                    className="w-full justify-between"
                    data-testid="quick-action-view-insights"
                  >
                    <span className="flex items-center gap-2">
                      <Lightbulb className="w-4 h-4" />
                      View All Insights
                    </span>
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                  <Button
                    onClick={() => navigate('/parent/messages')}
                    variant="outline"
                    className="w-full justify-between"
                  >
                    <span className="flex items-center gap-2">
                      <MessageCircle className="w-4 h-4" />
                      Messages
                    </span>
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                  <Button
                    onClick={() => selectedChild && navigate(`/parent/goals/${selectedChild.id}`)}
                    variant="outline"
                    className="w-full justify-between"
                    disabled={!selectedChild}
                  >
                    <span className="flex items-center gap-2">
                      <Target className="w-4 h-4" />
                      Goals & Progress
                    </span>
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                  <Button
                    onClick={() => selectedChild && navigate(`/parent/rewards/${selectedChild.id}`)}
                    variant="outline"
                    className="w-full justify-between"
                    disabled={!selectedChild}
                  >
                    <span className="flex items-center gap-2">
                      <Award className="w-4 h-4" />
                      Rewards & Badges
                    </span>
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Monthly Reports */}
          {monthlyReports.filter(r => r.child_id === selectedChild?.id).length > 0 && (
            <Card className="shadow-lg border-0 border-l-4 border-l-orange-500">
              <CardHeader className="pb-2">
                <div className="flex justify-between items-center">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <BarChart2 className="w-5 h-5 text-orange-500" />
                    Monthly Reports
                  </CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {monthlyReports
                    .filter(r => r.child_id === selectedChild?.id)
                    .slice(0, 3)
                    .map(r => (
                      <div
                        key={r.id}
                        onClick={() => navigate(`/parent/monthly-report/${r.id}`)}
                        className="flex items-center justify-between p-4 bg-orange-50 rounded-xl border border-orange-100 cursor-pointer hover:bg-orange-100 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-orange-400 to-rose-500 flex items-center justify-center flex-shrink-0">
                            <BarChart2 className="w-5 h-5 text-white" />
                          </div>
                          <div>
                            <p className="font-semibold text-gray-900 text-sm">{r.month_label}</p>
                            <p className="text-xs text-gray-500">{r.report_count} session{r.report_count !== 1 ? 's' : ''} · {r.metadata?.dominant_mood || 'N/A'} mood</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="flex items-center gap-1 text-xs text-green-700 bg-green-100 px-2 py-0.5 rounded-full">
                            <CheckCircle className="w-3 h-3" /> New
                          </span>
                          <ChevronRight className="w-4 h-4 text-orange-400" />
                        </div>
                      </div>
                    ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Recent Insights (if any) */}
          {selectedChild.parent_insights && selectedChild.parent_insights.length > 0 && (
            <Card className="mt-6 border-l-4 border-orange-500 shadow-lg">
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Lightbulb className="w-5 h-5 text-orange-600" />
                    Recent Insights
                  </CardTitle>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate('/parent/daily-insight')}
                    className="text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                    data-testid="recent-insights-view-all"
                  >
                    View All <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {selectedChild.parent_insights.slice(0, 3).map((insight, index) => (
                    <div
                      key={insight.id || index}
                      onClick={() => navigate('/parent/daily-insight')}
                      className="p-4 bg-orange-50 rounded-lg border border-orange-100 cursor-pointer hover:bg-orange-100 transition-colors"
                      data-testid={`recent-insight-card-${index}`}
                    >
                      <p className="text-gray-800 text-sm line-clamp-3">{insight.content || insight.summary}</p>
                      {insight.principal_feedback_for_parent && (
                        <div className="mt-2 pt-2 border-t border-orange-200">
                          <p className="text-[10px] uppercase tracking-wide text-orange-700 font-semibold mb-0.5">
                            Principal's note
                          </p>
                          <p className="text-xs text-gray-700 italic line-clamp-2">
                            “{insight.principal_feedback_for_parent}”
                          </p>
                        </div>
                      )}
                      <p className="text-xs text-orange-600 mt-2">
                        {new Date(insight.created_at).toLocaleDateString('en-US', {month: 'short', day: 'numeric'})}
                      </p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Empty State - No Children */}
      {children.length === 0 && (
        <div className="max-w-7xl mx-auto px-4 pb-12">
          <Card className="text-center py-12 shadow-lg">
            <CardContent>
              <User className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-gray-700 mb-2">No Children Enrolled Yet</h3>
              <p className="text-gray-500 mb-6">
                Your children will appear here once they are enrolled in the Sanjaya program.
              </p>
              <Button onClick={() => navigate('/enroll')} className="bg-gradient-to-r from-orange-500 to-rose-500 hover:from-orange-600 hover:to-rose-600 text-white">
                Enroll Your Child
              </Button>
            </CardContent>
          </Card>
        </div>
      )}
        </>
      )}
    </div>
  );
};

export default ParentDashboard;
